import { upsertUserAsync } from "../api";
import { ENTRA, KEYCLOAK, getConfiguredProviders } from "./providers";

export { ENTRA, KEYCLOAK } from "./providers";

const SelectedProviderStorageKey = "gtb.tournaments-admin.auth-provider";

export const AuthStatus = {
  Authenticated: "authenticated",
  Unauthorized: "unauthorized",
  ChooseProvider: "choose-provider",
};

let provider = null;

const loadProviderAsync = async (name) => {
  switch (name) {
    case KEYCLOAK:
      return await import("./keycloakProvider");
    case ENTRA:
      return await import("./entraProvider");
    default:
      throw new Error(`Unknown authentication provider: ${name}`);
  }
};

const readSelectedProvider = (providers) => {
  try {
    const selected = sessionStorage.getItem(SelectedProviderStorageKey);
    return providers.includes(selected) ? selected : null;
  } catch {
    return null;
  }
};

/**
 * Get the providers the user can sign in with.
 * @returns Provider names.
 */
export function getAvailableProviders() {
  return getConfiguredProviders();
}

/**
 * Select the provider to sign in with (when more than one is available).
 * @param {string} name - Provider name.
 */
export function selectProvider(name) {
  try {
    sessionStorage.setItem(SelectedProviderStorageKey, name);
  } catch {
    // ignore, the choice is asked again
  }
}

/**
 * Get the user information.
 * @returns .
 */
export function getUser() {
  return provider ? provider.getUser() : { authenticated: false };
}

/**
 * Get user authentication token.
 * @returns .
 */
export async function getUserTokenAsync() {
  return provider ? await provider.getUserTokenAsync() : null;
}

/**
 * Log out the user.
 * @returns .
 */
export async function logOutAsync() {
  try {
    sessionStorage.removeItem(SelectedProviderStorageKey);
  } catch {
    // ignore
  }

  return provider ? await provider.logOutAsync() : undefined;
}

/**
 * Authenticate the user.
 * @returns {Promise<string>} One of AuthStatus.
 */
export async function authenticateAsync() {
  const providers = getConfiguredProviders();

  const name =
    providers.length === 1 ? providers[0] : readSelectedProvider(providers);

  if (!name) return AuthStatus.ChooseProvider;

  try {
    provider = await loadProviderAsync(name);

    const authenticated = await provider.authenticateAsync();
    if (!authenticated) return AuthStatus.Unauthorized;
  } catch (error) {
    console.error("Authentication failed", error);
    return AuthStatus.Unauthorized;
  }

  //
  // This fire-and-forget is intentional. We don't want to block the user from
  // accessing the app while we're updating the user information.
  upsertUserAsync();

  return AuthStatus.Authenticated;
}
